import React from "react"
import PropTypes from "prop-types"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider
} from "@mui/material"
import { LockOutlined } from "@mui/icons-material"
import moment from "moment"
import { formatPrice } from "./price"

/**
 * Purchase confirmation. `onConfirm` enrols a free course directly, or opens
 * Razorpay Checkout for a paid one (see usePurchaseFlow).
 */
export default function CheckoutDialog({ open, course, saving, onClose, onConfirm }) {
    if (!course) return null

    const isFree = Number(course.price) <= 0

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            sx={{
                "& .MuiDialog-paper": {
                    m: { xs: 2, sm: 4 },
                    borderRadius: 3
                }
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isFree ? "Enrol in this course" : "Confirm your purchase"}
            </DialogTitle>

            <DialogContent dividers>
                <h3 className="text-sm font-semibold text-slate-800">
                    {course.title}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                    {[
                        course.class_name,
                        course.subject_name,
                        `${course.lecture_count || 0} lectures`
                    ]
                        .filter(Boolean)
                        .join(" · ")}
                </p>

                <Divider sx={{ my: 2 }} />

                <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Total</span>
                    <span className="text-lg font-bold text-slate-900">
                        {formatPrice(course.price, course.currency)}
                    </span>
                </div>

                <div className="mt-4 flex justify-between text-sm">
                    <span className="text-slate-600">Access</span>
                    <span className="font-medium text-slate-800">
                        1 year, until {moment().add(1, "year").format("D MMM YYYY")}
                    </span>
                </div>

                <p className="mt-4 flex items-start gap-1.5 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                    <LockOutlined sx={{ fontSize: 15 }} />
                    <span>
                        {isFree
                            ? "Every lecture unlocks straight away. After a year the course leaves My Courses."
                            : "You'll pay securely with Razorpay (UPI, cards, net banking or wallets). Every lecture unlocks as soon as the payment goes through; after a year the course leaves My Courses."}
                    </span>
                </p>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onConfirm(course)}
                    disabled={saving}
                >
                    {isFree
                        ? "Enrol now"
                        : `Pay ${formatPrice(course.price, course.currency)}`}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

CheckoutDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    course: PropTypes.object,
    saving: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
}
